
import { useState, useEffect } from "react"
import Calendar from "react-calendar"
import { FaPlus, FaStickyNote } from "react-icons/fa"
import { useAppContext } from "../context/AppContext"
import ShiftModal from "../components/ShiftModal"
import ShiftList from "../components/ShiftList"
import "react-calendar/dist/Calendar.css"
import "./CalendarPage.css" 
import ObservationList from "../components/ObservationList"
import { useObservations } from "../context/ObservationContext"
import { AiOutlineLoading } from "react-icons/ai"

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [formattedDate, setFormattedDate] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingShift, setEditingShift] = useState(null)
  const { shifts, tags, getShiftsForDate } = useAppContext()
  const { hasObservationsOnDate } = useObservations()
  const [shiftsForDate, setShiftsForDate] = useState([])

  const {loadingApp} = useAppContext()
  
  
  useEffect(() => {
    const dateString = selectedDate.toISOString().split("T")[0]
    setFormattedDate(formatDate(selectedDate))
    setShiftsForDate(getShiftsForDate(dateString))
  }, [selectedDate, shifts, getShiftsForDate])

  const formatDate = (date) => {
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    })
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)
  }

  const handleAddShift = () => {
    setEditingShift(null)
    setIsModalOpen(true)
  }
  
  const handleEditShift = (shift) => {
    setEditingShift(shift)
    setIsModalOpen(true)
  }
  
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingShift(null)
  }
  
  // Function to generate tile content for the calendar
  const tileContent = ({ date, view }) => {
    if (view !== "month") return null
    
    const dateString = date.toISOString().split("T")[0]
    const shiftsOnDate = shifts.filter((shift) => shift.date === dateString)
    
    const hasObservations = hasObservationsOnDate(dateString)
    if (shiftsOnDate.length === 0) return hasObservations ? <FaStickyNote size="10px" /> : null

    // Get unique tag colors for this date
    const uniqueTags = []
    shiftsOnDate.forEach((shift) => {
      const tag = tags.find((t) => t.id === shift.tagId)
      if (tag && !uniqueTags.some((t) => t.id === tag.id)) {
        uniqueTags.push(tag)
      }
    })
    // Create multi-color indicator based on number of unique tags
    if (uniqueTags.length > 1) {
      return (
        <>
          {hasObservations ? <FaStickyNote /> : null}
          <div className="multi-color-indicator">
            {uniqueTags.map((tag, index) => (
              <div
              key={index}
              className="color-segment"
              style={{
                backgroundColor: tag.color,
                width: `${100 / uniqueTags.length}%`,
                  left: `${(100 / uniqueTags.length) * index}%`,
                }}
                title={tag.name}
              />
            ))}
          </div>
        </>

)
} else if (uniqueTags.length === 1) {
  // Single color indicator
  return (
    <>
        {hasObservations ? <FaStickyNote /> : null}
          <div
            className="single-color-indicator"
            style={{ backgroundColor: uniqueTags[0].color }}
            title={uniqueTags[0].name}
          />
        </>
      )
    }
    
    return hasObservations ? <FaStickyNote /> : null
  }



  // Function to apply custom class names to tiles
  const tileClassName = ({ date, view }) => {
    if (view !== "month") return null

    const dateString = date.toISOString().split("T")[0]
    // Check if this date has shifts or observations
    const shiftsOnDate = shifts.filter((shift) => shift.date === dateString)
    const hasObservations = hasObservationsOnDate(dateString)
    
    if (shiftsOnDate.length === 0 && !hasObservations) return null

    // Classes baseadas no conteúdo do dia
    const classes = ["has-content"]
    
    if (shiftsOnDate.length > 0) {
      classes.push("has-shifts")
      
      // Get unique tag colors for this date
      const uniqueTags = []
      shiftsOnDate.forEach((shift) => {
        const tag = tags.find((t) => t.id === shift.tagId)
        if (tag && !uniqueTags.some((t) => t.id === tag.id)) {
          uniqueTags.push(tag)
        }
      })
      
      classes.push(`tags-${uniqueTags.length}`)
    }
    
    if (hasObservations) {
      classes.push("has-observations")
    }
    
    return classes.join(" ")
  }

  return (
    <div className="calendar-page">
      {loadingApp && <div className="loading"><AiOutlineLoading size="50px"/></div>}
      
      <div className="page-header">
        <h1 className="page-title">Calendário</h1>
        <button className="btn btn-primary" onClick={handleAddShift}>
          <FaPlus className="icon-left" /> Adicionar Plantão
        </button>
      </div>

      <div className="calendar-container">
        <div className="calendar-wrapper">
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            tileContent={tileContent}
            tileClassName={tileClassName}
            locale="pt-BR"
          />

          <div className="calendar-legend">
            <h4>Etiquetas</h4>
            <div className="legend-items">
              {tags.map((tag) => (
                <div key={tag.id} className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: tag.color }}></div>
                  <div className="legend-name">{tag.name}</div>
                </div>
              ))}
              <div className="legend-item">
                <div className="legend-icon">
                  <FaStickyNote />
                </div>
                <div className="legend-name">Observações</div>
              </div>
            </div>
          </div>
        </div>

        <div className="shifts-container">
          <div className="shifts-header">
            <h2>{formattedDate}</h2>
            <button className="btn btn-primary btn-sm" onClick={handleAddShift}>
              <FaPlus />
            </button>
          </div>

          {shiftsForDate.length > 0 ? (
            <ShiftList shifts={shiftsForDate} onEditShift={handleEditShift} />
          ) : (
            <div className="no-shifts">
              <p>Nenhum plantão agendado para esta data</p>
              <button className="btn btn-primary" onClick={handleAddShift}>
                <FaPlus className="icon-left" /> Adicionar Plantão
              </button>
            </div>
          )}
        </div>
      </div>

      <ObservationList selectedDate={selectedDate.toISOString().split("T")[0]} />

      {isModalOpen && (
        <ShiftModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          selectedDate={selectedDate.toISOString().split("T")[0]}
          shift={editingShift}
        />
      )}
    </div>
  )
}

export default CalendarPage

